import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { AgreementRef } from "./AgreementRef";
import type { BalanceType } from "./BalanceType";
import type { CatalogDiscountRef } from "./CatalogDiscountRef";
import type { CatalogItemAgreementStaff } from "./CatalogItemAgreementStaff";
import type { CatalogItemAgreementsStaffAssignmentInput } from "./CatalogItemAgreementsStaffAssignmentInput";
import type { CatalogItemCategoriesStaffAssignmentInput } from "./CatalogItemCategoriesStaffAssignmentInput";
import type { CatalogItemCategoryStaff } from "./CatalogItemCategoryStaff";
import type { CatalogItemDTOBase } from "./CatalogItemDTOBase";
import type { CatalogItemDiscountStaff } from "./CatalogItemDiscountStaff";
import type { CatalogItemInstanceStaff } from "./CatalogItemInstanceStaff";
import type { CatalogItemItemDiscountsStaffAssignmentInput } from "./CatalogItemItemDiscountsStaffAssignmentInput";
import type { CatalogItemTaxStaff } from "./CatalogItemTaxStaff";
import type { CatalogItemTaxesStaffAssignmentInput } from "./CatalogItemTaxesStaffAssignmentInput";
import type { CatalogRef } from "./CatalogRef";
import type { CatalogStaff } from "./CatalogStaff";
import type { CatalogTaxRef } from "./CatalogTaxRef";
import type { CategoryRef } from "./CategoryRef";
import type { ItemType } from "./ItemType";
import type { ProductRef } from "./ProductRef";
import type { ProductStaff } from "./ProductStaff";
import type { TrackedModernBase } from "./TrackedModernBase";

export class CatalogItemStaff implements CatalogItemDTOBase, TrackedModernBase {
  constructor(public readonly assignId: number, public readonly catalogId: CatalogStaff | null, public readonly itemType: ItemType | null, public readonly itemName: string | null, public readonly itemDescription: string | null, public readonly imageUrl: string | null, public readonly itemSku: string | null, public readonly minQuantity: number | null, public readonly maxQuantity: number | null, public readonly allowPriceOverride: boolean | null, public readonly priceOverrideMin: number | null, public readonly priceOverrideMax: number | null, public readonly squareItemId: string | null, public readonly expirationDurationSeconds: number | null, public readonly classTypeId: number | null, public readonly classInstanceId: number | null, public readonly membershipTypeId: number | null, public readonly damageWaiverTypeId: number | null, public readonly balanceType: BalanceType | null, public readonly itemInstances: Array<CatalogItemInstanceStaff> | null, public readonly categories: Array<CatalogItemCategoryStaff> | null, public readonly agreements: Array<CatalogItemAgreementStaff> | null, public readonly taxes: Array<CatalogItemTaxStaff> | null, public readonly itemDiscounts: Array<CatalogItemDiscountStaff> | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly productId: ProductStaff | null, public readonly catalogIdTargetIds: CatalogRef | null, public readonly productIdTargetIds: ProductRef | null) {
    this.cbiServiceContext = null;
    this.catalogIdLoaded = this.catalogId != null;
    this.catalogIdCached = this.catalogId;
    this.itemInstancesLoaded = this.itemInstances != null;
    this.itemInstancesCached = this.itemInstances;
    this.categoriesLoaded = this.categories != null;
    this.categoriesCached = this.categories;
    this.agreementsLoaded = this.agreements != null;
    this.agreementsCached = this.agreements;
    this.taxesLoaded = this.taxes != null;
    this.taxesCached = this.taxes;
    this.itemDiscountsLoaded = this.itemDiscounts != null;
    this.itemDiscountsCached = this.itemDiscounts;
    this.productIdLoaded = this.productId != null;
    this.productIdCached = this.productId;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private catalogIdLoaded: boolean;
  private catalogIdCached: CatalogStaff | null;
  private itemInstancesLoaded: boolean;
  private itemInstancesCached: Array<CatalogItemInstanceStaff> | null;
  private categoriesLoaded: boolean;
  private categoriesCached: Array<CatalogItemCategoryStaff> | null;
  private agreementsLoaded: boolean;
  private agreementsCached: Array<CatalogItemAgreementStaff> | null;
  private taxesLoaded: boolean;
  private taxesCached: Array<CatalogItemTaxStaff> | null;
  private itemDiscountsLoaded: boolean;
  private itemDiscountsCached: Array<CatalogItemDiscountStaff> | null;
  private productIdLoaded: boolean;
  private productIdCached: ProductStaff | null;
  async catalogIdLazy(): Promise<CatalogStaff | null> {
    if (!this.catalogIdLoaded) {
      this.catalogIdCached = await (this.catalogIdTargetIds != null ? this.cbiServiceContext!.catalogService().getCatalogStaff(this.catalogIdTargetIds.assignId) : Promise.resolve(null));
      this.catalogIdLoaded = true;
    }
    return this.catalogIdCached;
  }
  async itemInstancesLazy(): Promise<Array<CatalogItemInstanceStaff>> {
    if (!this.itemInstancesLoaded) {
      this.itemInstancesCached = await this.cbiServiceContext!.catalogService().getCatalogItemStaffItemInstances(this.assignId);
      this.itemInstancesLoaded = true;
    }
    return this.itemInstancesCached!;
  }
  async categoriesLazy(): Promise<Array<CatalogItemCategoryStaff>> {
    if (!this.categoriesLoaded) {
      this.categoriesCached = await this.cbiServiceContext!.catalogService().getCatalogItemStaffCategories(this.assignId);
      this.categoriesLoaded = true;
    }
    return this.categoriesCached!;
  }
  async agreementsLazy(): Promise<Array<CatalogItemAgreementStaff>> {
    if (!this.agreementsLoaded) {
      this.agreementsCached = await this.cbiServiceContext!.catalogService().getCatalogItemStaffAgreements(this.assignId);
      this.agreementsLoaded = true;
    }
    return this.agreementsCached!;
  }
  async taxesLazy(): Promise<Array<CatalogItemTaxStaff>> {
    if (!this.taxesLoaded) {
      this.taxesCached = await this.cbiServiceContext!.catalogService().getCatalogItemStaffTaxes(this.assignId);
      this.taxesLoaded = true;
    }
    return this.taxesCached!;
  }
  async itemDiscountsLazy(): Promise<Array<CatalogItemDiscountStaff>> {
    if (!this.itemDiscountsLoaded) {
      this.itemDiscountsCached = await this.cbiServiceContext!.catalogService().getCatalogItemStaffItemDiscounts(this.assignId);
      this.itemDiscountsLoaded = true;
    }
    return this.itemDiscountsCached!;
  }
  async productIdLazy(): Promise<ProductStaff | null> {
    if (!this.productIdLoaded) {
      this.productIdCached = await (this.productIdTargetIds != null ? this.cbiServiceContext!.catalogService().getProductStaff(this.productIdTargetIds.assignId) : Promise.resolve(null));
      this.productIdLoaded = true;
    }
    return this.productIdCached;
  }
  async addCategories(values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().addCatalogItemStaffCategories({ assignId: this.assignId }, values);
  }
  async putCategories(values: Array<CatalogItemCategoriesStaffAssignmentInput>): Promise<Array<CatalogItemCategoryStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().putCatalogItemStaffCategories({ assignId: this.assignId }, values);
  }
  async removeCategories(values: Array<CategoryRef>): Promise<Array<CatalogItemCategoryStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().removeCatalogItemStaffCategories({ assignId: this.assignId }, values);
  }
  async clearCategories(): Promise<Array<CatalogItemCategoryStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().clearCatalogItemStaffCategories({ assignId: this.assignId });
  }
  async addAgreements(values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().addCatalogItemStaffAgreements({ assignId: this.assignId }, values);
  }
  async putAgreements(values: Array<CatalogItemAgreementsStaffAssignmentInput>): Promise<Array<CatalogItemAgreementStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().putCatalogItemStaffAgreements({ assignId: this.assignId }, values);
  }
  async removeAgreements(values: Array<AgreementRef>): Promise<Array<CatalogItemAgreementStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().removeCatalogItemStaffAgreements({ assignId: this.assignId }, values);
  }
  async clearAgreements(): Promise<Array<CatalogItemAgreementStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().clearCatalogItemStaffAgreements({ assignId: this.assignId });
  }
  async addTaxes(values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().addCatalogItemStaffTaxes({ assignId: this.assignId }, values);
  }
  async putTaxes(values: Array<CatalogItemTaxesStaffAssignmentInput>): Promise<Array<CatalogItemTaxStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().putCatalogItemStaffTaxes({ assignId: this.assignId }, values);
  }
  async removeTaxes(values: Array<CatalogTaxRef>): Promise<Array<CatalogItemTaxStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().removeCatalogItemStaffTaxes({ assignId: this.assignId }, values);
  }
  async clearTaxes(): Promise<Array<CatalogItemTaxStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().clearCatalogItemStaffTaxes({ assignId: this.assignId });
  }
  async addItemDiscounts(values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().addCatalogItemStaffItemDiscounts({ assignId: this.assignId }, values);
  }
  async putItemDiscounts(values: Array<CatalogItemItemDiscountsStaffAssignmentInput>): Promise<Array<CatalogItemDiscountStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().putCatalogItemStaffItemDiscounts({ assignId: this.assignId }, values);
  }
  async removeItemDiscounts(values: Array<CatalogDiscountRef>): Promise<Array<CatalogItemDiscountStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().removeCatalogItemStaffItemDiscounts({ assignId: this.assignId }, values);
  }
  async clearItemDiscounts(): Promise<Array<CatalogItemDiscountStaff>> {
    if (!this.cbiServiceContext) throw new Error("No service context bound for assignment mutation.");
    return this.cbiServiceContext.catalogService().clearCatalogItemStaffItemDiscounts({ assignId: this.assignId });
  }
}
